import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X } from "lucide-react";
import toast from "react-hot-toast";
import { useLanguage } from "../context/LanguageContext";

import api from "../api/axios";

export default function CategoryModal({
    user,
    onComplete,
    // New Controlled Props
    isOpen: controlledIsOpen,
    onClose: controlledOnClose,
    onSelect: controlledOnSelect,
    initialSelected = [],
    isProviderView = false,
    isCertified = false // New prop to control high-risk access explicitly
}) {
    const { t } = useLanguage();
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);

    // Determine effective state (Controlled vs Uncontrolled)
    const isModalOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

    useEffect(() => {
        // Sync initial selection
        if (controlledIsOpen && initialSelected.length > 0) {
            setSelected(initialSelected);
        }
    }, [controlledIsOpen, initialSelected]);

    useEffect(() => {
        // Load categories on mount if not loaded
        loadCategories();
    }, []);

    useEffect(() => {
        // ONBOARDING MODE: Check if user already has categories
        // Only run if NOT in controlled mode
        if (controlledIsOpen === undefined && user) {
            const userCats = user.seeker?.categories || user.provider?.categories || [];
            if (userCats.length === 0) {
                setInternalIsOpen(true);
            }
        }
    }, [user, controlledIsOpen]);

    const loadCategories = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/categories`);
            setCategories(res.data);
        } catch (err) {
            console.error("Failed to load categories", err);
            toast.error("The Sanctuary's index is currently unavailable. Please try again soon.");
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (cat) => {
        // Check restriction
        const providerCertified = isCertified || user?.provider?.isCertified;
        const role = user?.role || (isProviderView ? "PROVIDER" : "SEEKER");

        if (
            role === "PROVIDER" &&
            !providerCertified &&
            cat.layer === "HIGH_RISK"
        ) {
            return; // Ignore click
        }

        setSelected((prev) => {
            if (prev.includes(cat.id)) {
                return prev.filter((id) => id !== cat.id);
            } else {
                return [...prev, cat.id];
            }
        });
    };

    const handleClose = () => {
        if (controlledOnClose) controlledOnClose();
        else setInternalIsOpen(false);
    };

    const handleSubmit = async () => {
        if (selected.length === 0) return;
        setLoading(true);

        if (controlledOnSelect) {
            // EXTERNAL HANDLER MODE (Profile Edit)
            await controlledOnSelect(selected);
            setLoading(false);
        } else {
            // ONBOARDING MODE (Self-contained)
            try {
                await api.post(`/categories/select`, { categoryIds: selected });
                setInternalIsOpen(false);
                if (onComplete) onComplete();
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }
    };

    if (!isModalOpen) return null;

    // Derived User Name for Greeting
    const userName = user?.name ? user.name.split(" ")[0] : "Friend";
    const userRole = user?.role || (isProviderView ? "PROVIDER" : "SEEKER");

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={handleClose} 
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                />

                {/* Modal */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]"
                >
                    {/* Close Button for Controlled Mode */}
                    {controlledIsOpen && (
                        <button
                            onClick={handleClose}
                            className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/30 text-white rounded-full z-10 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    )}

                    {/* Header */}
                    <div className="bg-brand-gradient p-8 text-center text-white shrink-0">
                        <h2 className="text-3xl font-display font-bold mb-2">
                            {controlledIsOpen ? t('cat_manage_title') : `Namaste, ${userName} 🙏`}
                        </h2>
                        <p className="text-blue-100 text-lg">
                            {userRole === "SEEKER"
                                ? t('cat_seeker_sub')
                                : t('cat_provider_sub')}
                        </p>
                    </div>

                    {/* Body */}
                    <div className="p-8 overflow-y-auto custom-scrollbar">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-h-[100px]">
                            {loading ? (
                                <div className="col-span-full flex flex-col items-center justify-center py-10 text-gray-400">
                                    <div className="w-8 h-8 rounded-full border-2 border-brand-blue/30 border-t-brand-blue animate-spin mb-4" />
                                    <p className="font-medium">Illuminating the paths...</p>
                                </div>
                            ) : categories.length === 0 ? (
                                <div className="col-span-full text-center py-12 px-6 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                                    <p className="text-gray-500 dark:text-gray-400 font-serif italic mb-2">
                                        "The Mirror reflects no paths at this moment."
                                    </p>
                                    <p className="text-xs text-gray-400 uppercase tracking-widest font-bold">No Categories Found</p>
                                </div>
                            ) : categories.map((cat) => {
                                const isHighRisk = cat.layer === "HIGH_RISK";
                                const providerCertified = isCertified || user?.provider?.isCertified;

                                const isDisabled =
                                    userRole === "PROVIDER" &&
                                    !providerCertified &&
                                    isHighRisk;

                                const isSelected = selected.includes(cat.id);

                                return (
                                    <button
                                        key={cat.id}
                                        onClick={() => handleToggle(cat)}
                                        disabled={isDisabled}
                                        className={`
                                            relative p-4 rounded-xl border text-left transition-all
                                            ${isDisabled ? "opacity-40 cursor-not-allowed bg-gray-50 border-gray-100" : "cursor-pointer hover:shadow-md"}
                                            ${isSelected
                                                ? "border-brand-blue bg-blue-50 dark:bg-brand-blue/20 ring-1 ring-brand-blue"
                                                : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                                            }
                                        `}
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <span className={`font-semibold ${isSelected ? "text-brand-blue" : "text-gray-900 dark:text-gray-100"}`}>
                                                {t(`cat_label_${cat.id}`) || cat.label}
                                            </span>
                                            {isSelected && <Check className="w-5 h-5 text-brand-blue" />}
                                            {isDisabled && <span className="text-xs text-red-400 font-medium">Certified Only</span>}
                                        </div>
                                        <div className="text-xs text-gray-500 font-medium leading-relaxed">
                                            {t(`cat_desc_${cat.id}`) || t(`cat_desc_${cat.layer}`) || cat.label}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 shrink-0 flex justify-end gap-3">
                        {controlledIsOpen && (
                            <button
                                onClick={handleClose}
                                className="px-6 py-3 rounded-xl text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
                            >
                                {t('ob_back')}
                            </button>
                        )}
                        <button
                            onClick={handleSubmit}
                            disabled={selected.length === 0 || loading}
                            className="px-8 py-3 rounded-xl bg-brand-blue text-white font-semibold shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? t('ob_starting') : (controlledIsOpen ? "Save Changes" : t('ob_finish'))}
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
